import { MedicareAccessScreenName } from '../config/FlowTypes';
import { StoreStateInternal } from '../store/Store';
import {
  hasAnsweredMedicareAccessState,
  hasOriginalMedicareAnswer,
  isEligibleForDirectMedicareRevCatGrant,
  isEligibleForFree65Access,
  isEligibleForMedicareAccess,
  isExcludedFromMedicareAccess,
  isMedicareOriginal,
} from './MedicareAccess';

export type StartGrantUs65PlusAccessOptions = {
  /** True when triggered from Us65PlusGrantAccessScreen, not login/register/userData merge. */
  fromGrantScreen?: boolean;
};

function hasMedicareHealthCoverAnswered(state: StoreStateInternal): boolean {
  const userData = state.userData as Partial<
    Record<
      | MedicareAccessScreenName.MedicareAccessHealthCover
      | MedicareAccessScreenName.MedicareAccessDirectHealthCover,
      string[]
    >
  >;
  return (
    userData[MedicareAccessScreenName.MedicareAccessHealthCover]?.[0] !== undefined ||
    userData[MedicareAccessScreenName.MedicareAccessDirectHealthCover]?.[0] !== undefined
  );
}

function hasCompletedMedicareAccessExclusion(state: StoreStateInternal): boolean {
  const userData = state.userData as Partial<
    Record<MedicareAccessScreenName.MedicareAccessExclusion, string[]>
  >;
  const answers = userData[MedicareAccessScreenName.MedicareAccessExclusion];
  return answers !== undefined && answers.length > 0;
}

/** Whether US 65+ free access should be granted for the current store state. */
export function shouldStartGrantUs65PlusAccess(
  state: StoreStateInternal,
  options?: StartGrantUs65PlusAccessOptions
): boolean {
  if (state.user.userId === undefined) {
    return false;
  }
  if (isExcludedFromMedicareAccess(state)) {
    return false;
  }
  if (!hasMedicareHealthCoverAnswered(state)) {
    return false;
  }
  // Until the state is known an Original user reads as not Original, which would grant a referred
  // user outside ACCESS and then exclude them from it.
  if (hasOriginalMedicareAnswer(state) && !hasAnsweredMedicareAccessState(state)) {
    return false;
  }
  if (isEligibleForFree65Access(state)) {
    if (isMedicareOriginal(state)) {
      if (!hasCompletedMedicareAccessExclusion(state)) {
        return false;
      }
      // ACCESS-path users are granted on Us65PlusGrantAccessScreen after CMS flow completes.
      if (!options?.fromGrantScreen && isEligibleForMedicareAccess(state)) {
        return false;
      }
      return true;
    }
    return true;
  }
  if (isEligibleForDirectMedicareRevCatGrant(state)) {
    return hasCompletedMedicareAccessExclusion(state);
  }
  return false;
}
